// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import loadBackOrders from "@guestsupply/App/Handlers/BackOrders/LoadBackOrders";
import updateBackorderParameter from "@guestsupply/App/Handlers/BackOrders/UpdateBackorderParameter";
import { GetBackOrdersParameters } from "@guestsupply/App/Services/BackorderManagementService";
import { CustomAppState } from "@guestsupply/App/Store/Reducers";
import { getBackorders, getBackordersParameter } from "@guestsupply/App/Store/Selectors/BackorderSelectors";
import Modals from "@guestsupply/Overrides/Components/Modals";
import parseQueryString from "@insite/client-framework/Common/Utilities/parseQueryString";
import Zone from "@insite/client-framework/Components/Zone";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import PageModule from "@insite/client-framework/Types/PageModule";
import PageProps from "@insite/client-framework/Types/PageProps";
import Page from "@insite/mobius/Page";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import isEqual from "lodash/isEqual"
import qs from "qs";
import React, { useEffect } from "react";
import { connect, ResolveThunks } from "react-redux";

const mapStateToProps = (state: CustomAppState) => {
    const backordersState = getBackorders(state, state.xBackorder.parameter!);
    return {
        ordersDataView: backordersState?.value,
        isLoading: backordersState?.isLoading,
        location: getLocation(state),
        backorderParameter: getBackordersParameter(state),
    };
};

const mapDispatchToProps = {
    loadBackOrders,
    updateBackorderParameter,
};

type Props = PageProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & HasHistory;

const BackorderManagementPage = ({
    loadBackOrders,
    updateBackorderParameter,
    ordersDataView,
    isLoading,
    id,
    history,
    location,
    backorderParameter,
}: Props) => {

    useEffect(() => {
        const parsedQuery = parseQueryString<GetBackOrdersParameters>(location.search);
        if (!isLoading && Object.keys(parsedQuery).length && !isEqual(parsedQuery, backorderParameter)) {
            updateBackorderParameter(parsedQuery);
        }
    }, []);

    useEffect(() => {
        if (backorderParameter && !ordersDataView && !isLoading) {
            loadBackOrders(backorderParameter);
        }
        const queryString = qs.stringify(backorderParameter);
        history.replace(`${location.pathname}${queryString !== "" ? `?${queryString}` : ""}`);
    }, [backorderParameter]);

    return (
        <Page data-test-selector="backorderManagement">
            <Zone contentId={id} zoneName="Content" />
            <Modals />
        </Page>
    );
};

const pageModule: PageModule = {
    component: withHistory(connect(mapStateToProps, mapDispatchToProps)(BackorderManagementPage)),
    definition: {
        hasEditableUrlSegment: true,
        hasEditableTitle: true,
        pageType: "System",
    },
};

export default pageModule;

/**
 * @deprecated Use string literal "BackorderManagementPage" instead of this constant.
 */
export const BackorderManagementPageContext = "BackorderManagementPage";
